import type { I18nDict } from '../types.ts'

export const en: I18nDict = {
  app_name: 'ZION',
  settings_title: 'Settings',
  section_language: 'Language',
  section_identity: 'Local identity',
  section_updates: 'App update',
  section_notifications: 'Notifications',
  section_theme: 'Visual theme',
  section_backup: 'Chat backup',
  section_privacy_network: 'Network privacy',
  section_media: 'Multimedia',
  section_security: 'Security & password',
  section_change_password: 'Change master password',
  section_2fa: 'Two-factor authentication (2FA)',
  section_sound: 'In-chat sounds',
  section_exit: 'Close & destroy data',
  settings_change_password_hint:
    'The new password re-encrypts keys and sensitive local data (AES-GCM vault).',
  settings_pwd_current: 'Current password',
  settings_pwd_new: 'New password',
  settings_pwd_confirm: 'Confirm new password',
  settings_btn_change_password: 'Update password',
  settings_pwd_working: 'Updating…',
  settings_pwd_ok: 'Password updated and vault re-encrypted ✓',
  settings_pwd_err_short: 'New password must be at least 8 characters.',
  settings_pwd_err_mismatch: 'New password and confirmation do not match.',
  settings_pwd_err_wrong: 'Current password is incorrect.',
  settings_pwd_err_identity: 'Incomplete identity — cannot encrypt vault.',
  settings_pwd_err_generic: 'Could not change password.',
  settings_2fa_hint:
    'Use Google Authenticator, Authy, or a compatible TOTP app. The secret stays on this device only.',
  settings_2fa_on: '2FA enabled',
  settings_2fa_off: '2FA disabled',
  settings_btn_enable_2fa: 'Enable 2FA (Google Authenticator / Authy)',
  settings_2fa_scan: 'Scan the QR with your authenticator app, then enter the 6-digit code.',
  settings_2fa_secret: 'Or enter this secret manually:',
  settings_2fa_confirm_code: 'Verification code (6 digits)',
  settings_btn_confirm_2fa: 'Confirm and enable 2FA',
  settings_btn_disable_2fa: 'Disable 2FA',
  settings_2fa_confirm_ok: '2FA enabled ✓',
  settings_2fa_confirm_fail: 'Invalid code — try again.',
  settings_2fa_disable_title: 'Disable 2FA?',
  settings_2fa_disable_body: 'Sign-in will again require only the master password.',
  settings_sound_hint: 'Volume of sound effects when you receive or send messages with the app open.',
  toggle_sound_mute: 'Mute / disable in-chat sounds',
  hint_sound_mute: 'When ON, no in-chat beeps (system notifications are unchanged).',
  settings_sound_volume: 'Effects volume',
  settings_btn_sound_test: 'Test sound',
  settings_sound_test_ok: 'Sound played.',
  settings_sound_test_fail: 'Could not play — check system volume and ensure mute is OFF.',
  auth_totp_title: '2FA code',
  auth_totp_hint: 'Enter the 6-digit code from your authenticator app.',
  auth_totp_code_label: 'TOTP code',
  auth_totp_wrong: 'Invalid 2FA code.',
  toggle_external_links: 'Receive and view external links',
  hint_external_links: 'If OFF: no link previews in chat and external URLs cannot be opened (IP tracking protection).',
  toggle_auto_media: 'Automatic media and document download',
  hint_auto_media: 'If OFF: photos, videos and files are not downloaded in background — tap manually in chat.',
  toggle_require_unlock: 'Password on app open',
  hint_require_unlock: 'When ON, the master password is required every time you open ZION.',
  toggle_wipe_exit: 'Wipe data on Exit',
  hint_wipe_exit: 'When ON, closing the app clears temporary RAM cache and session data.',
  btn_exit: 'Exit',
  btn_delete_all: 'DELETE ALL DATA',
  btn_lock: 'Lock app',
  btn_back: 'Back',
  warn_disable_password_title: '⚠️ WARNING',
  warn_disable_password_body:
    'Disabling the open password means anyone with your device can read your encrypted messages. Are you sure you want to continue?',
  warn_delete_all_1: 'Do you really want to destroy the entire database? This action is irreversible.',
  warn_delete_all_2: 'Final confirmation: all chats, private keys and settings will be permanently deleted.',
  link_blocked: 'External link blocked (privacy settings).',
  lang_welcome_title: 'Welcome to ZION',
  lang_welcome_sub: 'Choose your language',
  lang_continue: 'Continue',
  settings_language_label: 'App language',

  auth_unlock_title: 'Unlock',
  auth_or_password: '— or unlock with password —',
  auth_password_master: 'Master password',
  auth_unlock_btn: 'Unlock',
  auth_verifying: 'Verifying…',
  auth_wrong_password: 'Wrong password',
  auth_unlocked: 'Unlocked ✓',

  auth_access_title: 'Join ZION',
  auth_beta_spots: 'Free beta · seats left:',
  auth_beta_full: 'Beta full ({max} users). Try again later.',
  auth_beta_free: 'Free beta · no license required',
  auth_or_create: '— or create a new identity —',
  auth_password_min: 'Password (min. 8 characters)',
  auth_password_confirm: 'Confirm password',
  auth_privacy_check: 'I have read the',
  auth_privacy_link: 'Privacy Policy',
  auth_terms_check: 'I accept the',
  auth_terms_link: 'Terms of Service',
  auth_password_local_hint: 'The password never leaves this device. No server account.',
  auth_create_identity: 'Create identity',
  auth_accept_legal_err: 'Accept Privacy and Terms to continue',
  auth_generating_keys: 'Generating keys and session…',
  auth_generating_identity: 'Generating anonymous identity…',
  auth_generating_detail: 'ECDH keys · nickname · socket session · ZN- code',
  auth_you_are: 'You are',
  auth_daily_suffix_hint: 'The daily suffix changes every UTC day. To pair, use the ZN- code on Home.',
  auth_enter: 'Enter ZION',
  auth_starting: 'Starting…',

  auth_restore_title: 'Restore backup',
  auth_restore_hint:
    'Already have an encrypted backup? Recover identity, contacts and chats (e.g. after clearing cache).',
  auth_restore_file: 'Backup file (.enc / .enc.json)',
  auth_restore_pass: 'Backup passphrase',
  auth_restore_pass_ph: 'Passphrase used at export',
  auth_restore_btn: 'Restore backup',
  auth_restore_pick_file: 'Select a backup file (.enc or .enc.json).',
  auth_restore_need_pass: 'Enter the passphrase.',
  auth_restoring: 'Restoring…',
  auth_file_prefix: 'File:',

  common_accept: 'Accept',
  common_reject: 'Reject',
  common_cancel: 'Cancel',
  common_accepting: 'Accepting…',
  common_error: 'Error',
  common_loading: 'Loading…',
  common_send: 'Send',
  common_copied: 'Copied ✓',
  common_copy_failed: 'Copy failed',
  common_ok: 'OK',
  common_today: 'Today',
  common_yesterday: 'Yesterday',
  common_sending: 'Sending…',

  home_zn_midnight_hint: 'Changes at UTC midnight (in {h}h {m}m)',
  home_temp_empty: 'No timed chats. Tap ⏱ on a contact to start one.',
  home_unread_aria: '{count} messages',
  home_temp_waiting: 'Waiting for acceptance…',
  home_temp_expires: 'Timed chat · expires in {remaining}',
  home_temp_cancel: 'Cancel',
  home_temp_fallback_label: 'timed chat',
  home_temp_invite_title: '⏱ Timed chat invite',
  home_temp_invite_body: '{nick} wants an ephemeral chat ({duration}).',
  home_temp_invite_hint: 'Accept to start exchanging messages that will disappear on expiry.',
  home_temp_accept: 'Accept',
  home_temp_reject: 'Reject',
  home_temp_accepting: 'Accepting…',
  home_contacts_empty: 'No contacts. Share your ZN- code or QR to pair.',
  home_temp_chat_aria: 'Timed chat',
  home_btn_pair_title: 'Pair',
  home_btn_settings_title: 'Settings',
  home_push_banner_title: 'Push notifications',
  home_push_banner_body: 'Get sound and banner alerts when a message arrives, even in the background.',
  home_push_banner_ios: 'On iPhone/iPad, push notifications only work after adding ZION to your <strong>Home Screen</strong> (Safari → Share → Add to Home Screen), then opening the app from the icon.',
  home_push_banner_btn: 'Enable push notifications',
  home_push_banner_add_home: 'How to add to Home Screen',
  home_push_banner_active: 'Push notifications are active.',
  home_push_banner_denied: 'Notifications blocked. Enable them in system Settings.',
  home_zn_title: "Today's ZN- code",
  home_btn_copy_zn: 'Copy ZN-',
  home_btn_open_pair: 'Pairing QR',
  home_section_temp: 'Timed chats',
  home_section_chat: 'Chats',
  home_loading: 'Loading…',
  home_pair_zn_title: 'Pair with ZN- code',
  home_btn_zn_pair: 'Send request',
  home_copied: 'Copied ✓',
  home_copy_failed: 'Copy failed',
  home_swipe_delete_title: 'Delete conversation',
  home_swipe_delete_body: 'Remove this conversation from your list?',
  home_swipe_delete_confirm: 'Delete',
  home_swipe_delete_cancel: 'Cancel',
  home_swipe_delete_label: 'Delete',
  home_pair_request_title: 'Contact request',
  home_pair_request_body: '{name} wants to chat.',
  home_pair_reject: 'Reject',
  home_pair_accept: 'Accept',
  home_pair_accepting: 'Accepting…',
  home_pair_accept_failed: 'Acceptance failed',
  home_zn_digits_err: 'Enter the 6 digits after ZN-.',
  home_sending: 'Sending…',

  chat_rename: 'Rename',
  chat_rename_matrix: '[RENAME]',
  chat_rename_title: 'Rename contact',
  chat_menu_title: 'Chat options',
  chat_loading: 'Loading messages…',
  chat_reply_to: 'Reply to',
  chat_cancel_title: 'Cancel',
  chat_voice_lock_hint: 'Swipe up to lock recording',
  chat_voice_locked: 'Recording locked — tap Done',
  chat_voice_stop: 'DONE',
  chat_voice_stop_title: 'Done',
  chat_voice_preview_label: 'Voice preview — listen before sending',
  chat_voice_send: 'SEND',
  chat_voice_send_title: 'Send',
  chat_timer_label: 'Timer',
  chat_timer_title: 'Message timer',
  chat_timer_off: 'OFF',
  chat_timer_15s: '15s',
  chat_timer_30s: '30s',
  chat_timer_1m: '1m',
  chat_timer_2m: '2m',
  chat_photo: 'Photo',
  chat_photo_title: 'Photo',
  chat_voice_hold: 'Hold to record',
  chat_voice_hold_title: 'Hold to record',
  chat_doc: 'Doc',
  chat_doc_title: 'Document',
  chat_attach_title: 'Attachments',
  chat_input_placeholder: 'E2E encrypted message…',
  chat_send: 'Send',
  chat_rename_modal_title: 'Rename contact',
  chat_rename_nickname: 'Nickname: @{nick}',
  chat_rename_placeholder: 'Custom name',
  chat_rename_cancel: 'Cancel',
  chat_rename_save: 'Save',
  chat_new_contact_title: 'New contact added!',
  chat_new_contact_body: 'Would you like to assign a name or nickname to this contact?',
  chat_new_contact_skip: 'Skip',
  dialog_send_pairing_title: 'Contact found',
  dialog_send_pairing_body: 'Do you want to send a pairing request to this user?',
  dialog_send_pairing_confirm: 'Send Pairing',
  chat_action_reply: '↩ Reply',
  chat_action_copy: '⎘ Copy',
  chat_action_delete_me: '🗑 Delete for me',
  chat_action_delete_all: '🗑 Delete for everyone',
  chat_action_cancel: 'Cancel',
  chat_pin: '📌 Pin chat to top',
  chat_unpin: '📌 Unpin from top',
  chat_pinned: 'Pinned chat',
  chat_menu_clear_me: '🗑 Clear chat (for me only)',
  chat_menu_clear_all: '🗑 Delete chat for everyone',
  chat_menu_cancel: 'Cancel',
  chat_doc_send_title: 'Send document',
  chat_doc_default_name: 'document',
  chat_doc_send_hint: 'The file will be compressed, E2E encrypted and sent. Max 5 MB.',
  chat_doc_cancel: 'Cancel',
  chat_doc_send_encrypted: 'Send encrypted',
  chat_doc_too_large: '⚠️ FILE TOO LARGE: The maximum security limit for documents is 5MB.',
  chat_doc_unsupported: 'Unsupported document type.',
  chat_copy_failed: 'Copy failed',
  chat_confirm_delete_me: 'Delete this message just for you?',
  chat_confirm_delete_all: 'Delete this message for everyone?',
  chat_confirm_clear_all: 'Delete the entire chat for everyone? This action is irreversible.',
  chat_confirm_clear_me: 'Clear the chat just for you?',
  chat_jump_new: '↓ New messages',
  chat_empty: 'No messages. Write something to start.',
  chat_voice_err_short: 'Voice message too short (minimum 1 second).',
  chat_voice_err_long: 'Voice message too long (maximum 2 minutes).',
  chat_voice_err_large: 'Voice message too large (max 3 MB).',
  chat_voice_err_start: 'Voice recording could not start.',
  chat_destroyed: '✉ Message read and destroyed',
  chat_destroyed_title: 'Message read and destroyed',
  chat_hold_options: 'Hold for options',
  chat_reply_fallback: 'Message',
  chat_timer_badge: '⏱ {seconds}s',
  chat_timer_badge_pending: '⏱ …',

  pair_zn_midnight_hint: 'The ZN- code changes at UTC midnight (in {h}h {m}m).',
  pair_copied: 'Copied ✓',
  pair_copy_failed: 'Copy failed',
  pair_title: 'Pairing',
  pair_zn_title: 'Your ZN- code today',
  pair_btn_copy_zn: 'Copy ZN- code',
  pair_tab_mine: 'My QR',
  pair_tab_scan: 'Scan / ZN-',
  pair_qr_aria: 'Pairing QR',
  pair_expires_in: 'Expires in',
  pair_btn_copy_payload: 'Copy payload',
  pair_btn_regen_qr: 'Regenerate QR',
  pair_qr_hint: 'Show this QR to the other device. Valid for 10 minutes — compatible with the Android app.',
  pair_expired: 'expired',
  pair_contact_zn_title: "Contact's ZN- code",
  pair_zn_prefix_hint: 'The <strong>ZN-</strong> prefix is already set — enter only the 6 digits.',
  pair_btn_zn_request: 'Send ZN- request',
  pair_paste_title: 'Or paste a ZION1:: QR',
  pair_paste_hint: 'Paste a <code>ZION1::</code> payload or upload a photo of the QR.',
  pair_paste_placeholder: 'ZION1::…',
  pair_btn_pick_qr: 'From photo / camera',
  pair_btn_connect_qr: 'Connect with QR',
  pair_zn_digits_err: 'Enter the 6 digits after ZN-.',
  pair_sending: 'Sending request…',
  pair_reading_qr: 'Reading QR…',
  pair_qr_not_found: 'QR code not found in the image.',
  pair_zn_read_ok: 'ZN- code read — confirm with Send request.',
  pair_qr_read_ok: 'QR read — confirm with Connect with QR.',
  pair_qr_read_err: 'Error reading QR.',

  settings_identity_hint: 'Password and keys stay on this browser. No server account.',
  settings_btn_add_home: 'Add to Home guide',
  settings_devices_title: 'Linked devices',
  settings_devices_hint: 'Multi-device Zero-Knowledge: keys travel E2E encrypted. The server is only a relay.',
  settings_btn_link_device: 'Link new device',
  settings_btn_sync_pc: 'Sync PC history',
  settings_no_devices: 'No linked devices.',
  settings_device_self: 'this device',
  settings_version_prefix: 'Version:',
  settings_ios_push_hint: '<strong>iPhone:</strong> notifications = PWA from Home + Banner/Sound ON. Build ≥ <code>iospush7</code>.',
  settings_btn_force_update: 'Force app update',
  settings_updating: 'Updating…',
  pwa_update_banner_text: 'A new version of ZION is available!',
  pwa_update_banner_btn: 'Update now',
  section_support: 'Support the project',
  settings_support_hint:
    'ZION is independent, ad-free, and does not sell your data. If you find it useful, a voluntary tip helps us improve the app.',
  settings_btn_support_coffee: 'Buy me a coffee on Buy Me a Coffee',
  settings_support_note: 'Optional donation. Thank you for your support!',
  settings_push_mode_prefix: 'Mode:',
  settings_push_hint_ios:
    '<strong>iPad/iPhone:</strong> notifications only work when opening ZION from the Home Screen icon (Share → Add to Home Screen). In regular Safari they are <em>not</em> available — even on iOS 17. After installing to Home, come back here and tap Enable notifications.',
  settings_btn_enable_push: 'Enable push notifications',
  settings_btn_reset_push: 'Reset & Re-enable Push Notifications',
  settings_btn_desktop_notify: 'Desktop notifications (PC)',
  settings_btn_disable_push: 'Disable notifications',
  settings_desktop_requesting: 'Requesting desktop permission…',
  settings_desktop_ok: 'Desktop notifications active (sound + popup when a message arrives).',
  settings_desktop_denied: 'Permission denied or not supported.',
  settings_push_no_session: 'Session not ready.',
  settings_push_requesting: 'Requesting permission…',
  settings_push_resetting: 'Resetting subscription and re-enabling…',
  settings_push_reset_ok: 'Notifications reset and re-enabled with a fresh endpoint.',
  settings_push_disabling: 'Disabling…',
  settings_push_active: 'Notifications active.',
  settings_push_disabled: 'Notifications disabled.',
  settings_theme_hint: 'Same themes as the ZION Android app.',
  settings_theme_recommended: 'Recommended',
  settings_font_title: 'Chat font',
  settings_font_hint: 'Applies instantly to messages and the composer.',
  settings_font_system: 'System default',
  settings_font_mono: 'Hacker monospace',
  settings_font_serif: 'Elegant serif',
  settings_font_rounded: 'Modern rounded',
  settings_font_condensed: 'Compact condensed',
  settings_font_script: 'Cursive / script',
  settings_font_cyber: 'Cyberpunk tech',
  settings_font_legible: 'High readability',
  settings_font_slab: 'Heavy slab serif',
  settings_font_terminal: 'Retro terminal',
  settings_backup_hint:
    'JSON file encrypted with a passphrase (compatible with Android v2 format). Save it on your PC: contacts, chats and web identity.',
  settings_backup_pass_label: 'Backup passphrase',
  settings_backup_pass_ph: 'Dedicated passphrase (min. 8 characters)',
  settings_btn_export_backup: 'Export backup',
  settings_backup_file_label: 'Restore from file',
  settings_backup_import_pass_label: 'Backup passphrase',
  settings_backup_import_pass_ph: 'Passphrase used at export',
  settings_btn_import_backup: 'Restore backup',
  settings_backup_file_prefix: 'File: {name}',
  settings_backup_pass_min: 'Passphrase must be at least 8 characters.',
  settings_backup_creating: 'Creating backup…',
  settings_backup_pick_file: 'Select a backup file (.enc or .enc.json).',
  settings_backup_need_pass: 'Enter the backup passphrase.',
  settings_backup_restoring: 'Restoring…',
  settings_backup_overwrite_confirm: 'Overwrite local data?',
  settings_modal_ok: 'OK',

  temp_duration_5m: '5 minutes',
  temp_duration_10m: '10 minutes',
  temp_duration_15m: '15 minutes',
  temp_duration_30m: '30 minutes',
  temp_duration_1h: '1 hour',
  temp_duration_6h: '6 hours',
  temp_duration_24h: '24 hours',
  temp_duration_7d: '7 days',
  temp_remaining_expired: 'Expired',
  temp_remaining_days: '{days}d {hours}h',
  temp_remaining_hours: '{hours}h {minutes}m',
  temp_remaining_minutes: '{minutes}m',
  temp_remaining_seconds: '{seconds}s',
  temp_screen_subtitle: 'Timed chat · ephemeral messages',
  temp_empty: 'No ephemeral messages.',
  temp_empty_hint: 'Write an ephemeral message. It will disappear when the timer expires.',
  temp_input_placeholder: 'Ephemeral message…',
  temp_send: 'Send',
  temp_waiting_accept: 'Waiting for {name} to accept the timed chat…',
  temp_waiting_placeholder: 'Waiting for acceptance…',
  temp_expires_in: 'Expires in {remaining}',
  temp_duration_title: 'Timed chat',
  temp_duration_with: 'With {name}',
  temp_duration_hint: 'Choose the duration — the chat will disappear from both devices.',
  temp_sending_invite: 'Sending invite…',

  voice_lock_hint: 'Swipe up to lock recording',
  voice_locked: 'Recording locked — tap Done',

  add_home_title: 'Add to Home Screen (iPhone / iPad)',
  add_home_guide_title: 'Safari guide',
  add_home_step1: 'Open <strong>Safari</strong> on this page (<code>zionyx.ch/app/</code>)',
  add_home_step2: 'Tap <strong>Share</strong> (square with an arrow pointing up)',
  add_home_step3: 'Choose <strong>Add to Home Screen</strong>',
  add_home_step4: 'Open ZION from the <strong>icon</strong> — not from a Safari tab',
  add_home_hint:
    'On iOS/iPadOS 16.4+ the Home Screen app supports Web Push notifications and the chat keyboard. After installing, go to <strong>Settings → Enable notifications</strong>.',
  add_home_btn_continue: 'Got it — continue',
  add_home_btn_skip: 'Skip for now',

  search_title: 'Search with ZN- code',
  search_label: 'Daily ZN- code',
  search_placeholder: 'ZN-123456',
  search_btn: 'Search',
  search_hint: "Enter the contact's ZN- code. No nickname search — identity is local only.",
  search_format_err: 'Format: ZN-123456',
  search_resolving: 'Resolving code…',
  search_invalid_code: "Invalid code — ask for an updated ZN- (reopen ZION on the other person's device)",
  search_session_found: 'session found',
  search_tap_to_send: 'Tap to send request',
  search_socket_not_ready: 'Socket session not ready',
  search_request_sent: 'Request sent ✓',
  search_request_failed: 'Sending failed',

  license_title: 'Web / iPhone license activation',
  license_heading: 'License',
  license_heading_updated: 'Updated terms',
  license_hint:
    'Enter the <strong>ZION-XXXX-XXXX-XXXX</strong> code purchased on zionyx.ch. It will be linked to this browser (local key).',
  license_code_label: 'License code',
  license_code_placeholder: 'ZION-XXXX-XXXX-XXXX',
  license_btn_activate: 'Activate license',
  license_beta_hint: 'Web beta: use a dedicated iPhone license if available.',
  license_accept_legal_err: 'Accept Privacy and Terms to continue',
  license_need_code: 'Enter the license code',
  license_verifying: 'Verifying license…',
  license_invalid_or_used: 'Invalid license or already used on another device',
  license_active: 'License active ✓',
  license_err_not_found: 'Invalid license code',
  license_err_conflict: 'License already linked to another device',
  license_err_rate_limit: 'Too many attempts — wait a minute',
  license_err_generic: 'License error',

  link_device_title: 'Link new device',
  link_device_hint:
    'On your <strong>PC</strong>, open Settings → Link new device: you will see the <strong>green QR</strong>. Then scan it below (camera or photo).',
  link_device_status_ready: '[READY]',
  link_device_upload_label: 'Upload QR photo',
  link_device_btn_camera: 'Open camera / gallery',
  link_device_placeholder: 'QR link or payload…',
  link_device_btn_send_vault: 'Send encrypted vault',
  link_device_err_invalid_qr: 'Invalid or expired QR.',
  link_device_status_invalid: '[INVALID QR]',
  link_device_status_sync_complete: '[SYNC COMPLETE]',
  link_device_status_transfer_failed: '[TRANSFER FAILED]',
  link_device_status_qr_detected: '[QR DETECTED — TRANSFERRING...]',
  link_device_status_tap_camera: '[TAP THE GREEN BUTTON — IPHONE CAMERA]',
  link_device_status_use_camera_below: '[USE CAMERA / GALLERY BELOW]',
  link_device_status_point_camera: '[POINT CAMERA AT PC QR...]',
  link_device_status_decoding: '[DECODING QR...]',
  link_device_status_qr_not_found: '[QR NOT FOUND]',
  link_device_status_decode_failed: '[DECODE FAILED]',
  link_device_status_opened_from_qr: '[OPENED FROM QR — STARTING...]',

  link_recv_title: 'Link from phone',
  link_recv_hint_compact:
    'Scan this <strong>green QR</strong> with your phone (Settings → Link new device). The vault is end-to-end encrypted.',
  link_recv_hint_auth:
    'Already have ZION on your phone? Scan this QR with the <strong>Camera</strong> — the PC links and signs in automatically.',
  link_recv_qr_alt: 'Device link QR',
  link_recv_copy_link: 'Copy link',
  link_recv_awaiting: '[AWAITING PHONE SCAN — TUNNEL OPEN...]',
  link_recv_failed: '[PAIRING FAILED]',
  link_recv_tunnel_lost:
    'Tunnel interrupted — the vault may still be in transit. Try scanning the QR again.',

  notify_title_prefix: 'ZION — {name}',
  notify_default_body: 'New encrypted message',
  notify_temp_title_prefix: 'ZION ⏱ {name}',
  notify_temp_default_body: 'Ephemeral message',

  force_update_title:
    '🚨 MANDATORY CRITICAL UPDATE — To keep using Zionyx securely, you must download the latest release.',
  force_update_desc: 'The installed client is not compatible with the minimum version active on the server.',
  force_update_versions: 'Installed: v{current} · Required: v{required}',
  force_update_btn: 'UPDATE NOW',
}
